import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';

import { PlaneExpandLess, PlaneExpandMore } from 'assets/assetsV1/iconComponents';
import LogoGifWithoutLabel from 'assets/gifs/logoWithoutLabel.gif';
import NetchexLoader from 'assets/gifs/netchexLoader.gif';

import { noOp } from 'constants/helper';
import { isNetchexLogin } from 'constants/netChexConstants';

import { Button } from 'uiToolkitV1/Button';
import { TextLabel } from 'uiToolkitV1/Typography';

import { setIsExpandedContainer } from './ExpandedContainerSlice';

const initialContextValue = {
  isExpanded: false,
  onExpandClick: noOp,
  onShrinkClick: noOp,
};

const ExpandableContainerContext = createContext(initialContextValue);

const TransientProps = ['isExpanded', 'sx', 'width', 'height', 'yDistFromTop', 'xDistFromLeft'];
const StyledExpandableContainer = styled(Box, {
  shouldForwardProp: (prop) => !TransientProps.includes(prop),
})(
  ({ theme, width, height, yDistFromTop, xDistFromLeft }) => `
    transition: all .3s cubic-bezier(0.22, 0.61, 0.36, 1);
    width: 100%;

    &.growAnimate {
      position: fixed;
      width: calc(100vw - 64px) !important;
      height: calc(100vh - 48px);
      top: ${yDistFromTop};
      left: ${xDistFromLeft};

      transform: translate( calc(32px - ${xDistFromLeft}) ,
      calc(  24px - ${yDistFromTop} ));
    background-color: white;
    box-shadow: ${theme.shadows.paper};
    z-index: 1200; //same as material ui drawer

      & > div {
        height: 100%;
      }

    }

    &.sheetStyles {
      border-radius: 8px;
    }
    &.shrinkAnimate {
      animation: .3s shrinkAnimate;
      animation-fill-mode: forwards;
    }

    @keyframes &.shrinkAnimate {
      from {
        position: fixed;
        width: calc(100vw - 48px) !important;
        height: calc(100vh - 48px);
        top: ${yDistFromTop};
        left: ${xDistFromLeft};
      }
      99% {
        position: fixed;
        width: ${width ? `${width}px` : 'auto'};
        height: ${height ? `${height}px` : 'auto'};
        top: ${yDistFromTop};
        left: ${xDistFromLeft};
      }

      to {
        top: ${yDistFromTop};
        left: ${xDistFromLeft}
        position: relative;
        width: ${width ? `${width}px` : 'auto'};
        height: ${height ? `${height}px` : 'auto'};
      }
    }

    `
);

const RelativeButtonBox = styled(Box)`
  position: absolute;
  top: 12px;
  right: 16px;
  height: 40px !important;
  backdrop-filter: blur(4px);
`;

const shrinkDomDataInitial = {
  width: 'auto',
  height: 'auto',
  top: '0px',
  left: '0px',
};

const Overlay = ({ handleShrinkClick }) => (
  <Box
    position="fixed"
    w="100vw"
    h="100vh"
    top="0"
    left="0"
    bg="rgba(0, 0, 0, 0.4)"
    backdropFilter="blur(1.5px)"
    onClick={(e) => {
      e.stopPropagation();
      handleShrinkClick();
    }}
    onScroll={(e) => e.stopPropagation()}
    zIndex="1000"
  />
);

export const ExpandableContainerProvider = ({
  children,
  id,
  isParentDeterminesHeight = false,
  toggleBtnBoxProps = {},
  onExpand = () => {},
  onShrink = () => {},
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // this is for async download operation
  const [isShowProgressLoader, setIsShowProgressLoader] = useState(false);
  const containerRef = useRef(null);
  const dummyContainerRef = useRef(null);

  const [shrinkDomData, setShrinkDomData] = useState(shrinkDomDataInitial);
  const [className, setClassName] = useState('');

  const dispatch = useDispatch();

  const handleExpandClick = useCallback(() => {
    if (isExpanded) return;
    onExpand();
    let boundingClientRect = {
      width: 0,
      height: 0,
      top: 0,
    };
    if (containerRef.current) {
      boundingClientRect = containerRef.current.getBoundingClientRect();
    }

    setShrinkDomData({
      width: boundingClientRect.width,
      height: boundingClientRect.height,
      top: `${boundingClientRect.top}px`,
      left: `${boundingClientRect.left}px`,
    });

    setIsExpanded(true);
    dispatch(setIsExpandedContainer(true));
    setClassName('growAnimate sheetStyles');
  }, [dispatch, isExpanded, onExpand]);

  const handleShrinkClick = useCallback(
    ({ forced = false } = {}) => {
      if (!isExpanded && !forced) return;
      onShrink();
      let boundingClientRect = {
        width: 0,
        height: 0,
        top: 0,
      };
      if (dummyContainerRef.current) {
        boundingClientRect = dummyContainerRef.current.getBoundingClientRect();
      }
      setShrinkDomData(() => ({
        width: boundingClientRect.width,
        height: boundingClientRect.height,
        top: `${boundingClientRect.top}px`,
        left: `${boundingClientRect.left}px`,
      }));

      setIsExpanded(false);
      dispatch(setIsExpandedContainer(false));
      setClassName('shrinkAnimate');
    },
    [dispatch, isExpanded, onShrink]
  );

  // this is to download the chart in expanded mode even when the download button is clicked in mini mode
  const handleMiniToExpandedOperationDownload = useCallback(
    (callbackFn) => {
      setIsShowProgressLoader(true);
      if (isExpanded) return;
      handleExpandClick();
      setTimeout(() => {
        callbackFn();
      }, 2000);
      setTimeout(() => {
        handleShrinkClick({ forced: true });
        setIsShowProgressLoader(false);
      }, 4000);
    },
    [handleExpandClick, handleShrinkClick, isExpanded]
  );

  const value = useMemo(
    () => ({
      isExpanded,
      onExpandClick: handleExpandClick,
      onShrinkClick: handleShrinkClick,
      onMiniToExpandedOperation: handleMiniToExpandedOperationDownload,
    }),
    [handleExpandClick, handleMiniToExpandedOperationDownload, handleShrinkClick, isExpanded]
  );
  return (
    <ExpandableContainerContext.Provider value={value}>
      {isExpanded && <Overlay handleShrinkClick={handleShrinkClick} />}
      <Box display="flex" key={id} h={isParentDeterminesHeight ? '100%' : 'auto'}>
        <StyledExpandableContainer
          className={className}
          ref={containerRef}
          // flexGrow={1}
          width={shrinkDomData.width}
          height={isParentDeterminesHeight ? '100%' : shrinkDomData.height}
          yDistFromTop={shrinkDomData.top}
          xDistFromLeft={shrinkDomData.left}
          key={id}
          pos="relative"
          // h={isParentDeterminesHeight ? '100%' : ''}
        >
          <RelativeButtonBox
            variant="tertiary"
            StartIcon={isExpanded ? PlaneExpandLess : PlaneExpandMore}
            // onClick={isExpanded ? handleShrinkClick : handleExpandClick}
            {...toggleBtnBoxProps}
          >
            <Button
              variant="tertiary"
              StartIcon={isExpanded ? PlaneExpandLess : PlaneExpandMore}
              onClick={isExpanded ? handleShrinkClick : handleExpandClick}
            />
          </RelativeButtonBox>
          {children}

          {isShowProgressLoader && (
            <Box
              display="flex"
              flexDir="column"
              width="calc(100vw - 64px)"
              height="calc(100vh - 48px)"
              justifyContent="center"
              alignItems="center"
              bg="white"
              position="absolute"
              top={0}
              left={0}
              borderRadius="8px"
            >
              <img src={isNetchexLogin ? NetchexLoader : LogoGifWithoutLabel} alt="Logo" width="100px" height="100px" />
              <TextLabel color="text.g2" pt={4}>
                Generating image...
              </TextLabel>
            </Box>
          )}
        </StyledExpandableContainer>

        {/* dummy container to fix height and prevent content jump while expanding main container */}

        <Box
          ref={dummyContainerRef}
          sx={{
            height: isParentDeterminesHeight ? '100%' : shrinkDomData.height,
            width: isExpanded ? shrinkDomData.width : '0',
          }}
          id={id}
        />
      </Box>
    </ExpandableContainerContext.Provider>
  );
};

export const useExpandableContainer = () => useContext(ExpandableContainerContext);
